import {
    Box,
    Button,
    FormHelperText,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    Form,
    Formik,
    FormikProps
} from "formik";
import OtpInput, { InputProps } from "react-otp-input";
import { MFSubmitButton } from ".";
import {
    useEffect,
    useRef,
    useState
} from "react";
import { InfoIconOutlined } from "../../assets/SvgIcons";

interface Values {
    otp: string;
}

const initialValues: Values = {
    otp: "",
};

const OTP_TIMER = 60

function OtpTimer({
    onResend,
    disabled
}: {
    onResend: () => void;
    disabled: boolean;
}): JSX.Element {
    const [counter, setCounter] = useState(OTP_TIMER);

    useEffect(() => {
        let timer: any = null

        if (counter > 0) {
            timer = setInterval(() => {
                setCounter(counter - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [counter]);

    const handleResend = async () => {
        try {
            if (counter === 0) {
                await onResend();
                setCounter(OTP_TIMER);
            }
        } catch (e) {
            console.error((e as Error).message);
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Box
                sx={(theme: Theme) => ({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    [theme.breakpoints.down("sm")]: {
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        whiteSpace: "nowrap",
                    },
                })}>
                <Typography
                    variant='body_400'
                    color="text.secondary"
                >
                    Didn’t receive OTP ?
                </Typography>
                <Button
                    variant="text"
                    sx={(theme: Theme) => ({
                        cursor: counter > 0 ? "not-allowed" : "pointer",
                        color: counter > 0 ? theme.palette.text.secondary : theme.palette.primary[500],
                        padding: "0px 5px",
                        ...theme.typography.body_400
                    })}
                    onClick={handleResend}
                    disabled={disabled || counter > 0}
                    aria-label={counter > 0 ? `Resend OTP available in ${counter} seconds` : 'Resend OTP'}
                >
                    Resend OTP
                </Button>
            </Box>
            {counter > 0 && (
                <Typography
                    variant='body_400'
                    color="text.secondary"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    in {counter} seconds
                </Typography>
            )}
        </Box>
    );
}

export default function OTPForm({
    onSubmit,
    onResend,
    otpMessage,
    warningMessage = "Note: User Account will be locked after 3 unsuccessful attempts, Resend otp can be requested 2 times for every hour"
}: {
    onSubmit: (otp: string) => void;
    onResend: () => void;
    otpMessage: string;
    warningMessage?: string;

}): JSX.Element {
    const theme = useTheme()
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const isXs = useMediaQuery(theme.breakpoints.up('xs'));
    const formikRef = useRef<FormikProps<Values>>(null);

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isSubmitting, setSubmitting] = useState<boolean>(false)

    const handleResendOtp = async () => {
        try {
            setLoading(true)
            await onResend()
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setLoading(false)
        }
    };

    const handleChange = (otp: string) => {
        formikRef?.current?.setValues({ otp })
    }

    const handleSubmit = async (
        values: Values,
        { resetForm }: { resetForm: () => void }) => {
        try {
            setSubmitting(true)

            const { otp = "" } = values;

            if (!otp || otp?.length !== 6) {
                formikRef?.current?.setErrors({ otp: 'Enter OTP' })
                return;
            }

            await onSubmit(otp)
            resetForm()
        } catch (e) {
            console.error((e as Error).message);
            resetForm()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ values, errors }) => (
                <Form noValidate>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500],
                            textAlign: 'center',
                            marginBottom: "20px",
                            [theme.breakpoints.down("sm")]: {
                                display: "-webkit-box"
                            },
                        })}
                    >
                        {otpMessage ?? ""}
                    </Typography>
                    <Box
                        sx={{
                            mb: 0,
                            '.otp-input': {
                                input: {
                                    width: { xs: "37px !important", sm: "45px !important", md: "45px !important" },
                                    fontSize: { xs: "22px !important", sm: "28px !important", md: "30px !important" },
                                    margin: { xs: "2px !important", sm: "2px !important", md: "4px !important" },
                                },
                            },
                            [theme.breakpoints.down("sm")]: {
                                display: "flex",
                                justifyContent: "center",
                                flexWrap: "wrap",
                                gap: "6px",
                            },
                        }}
                    >
                        <OtpInput
                            inputType='number'
                            renderInput={(inputProps: InputProps, index: number) => <input {...inputProps} type='password' style={{ ...inputProps.style }} disabled={isLoading || isSubmitting} aria-label={`OTP digit ${index + 1} of 6`} />}
                            value={values.otp}
                            // isDisabled={isLoading || isSubmitting}
                            onChange={handleChange}
                            numInputs={6}
                            // hasErrored={!!errors?.otp}
                            // isInputNum={true}
                            // isInputSecure={true}
                            shouldAutoFocus={true}
                            containerStyle={{
                                display: 'flex',
                                justifyContent: 'center',
                                // flexWrap: 'wrap',
                            }}
                            inputStyle={{
                                width: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                height: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                padding: '5px 10px',
                                borderRadius: '5px',
                                fontSize: '30px',
                                borderColor: errors?.otp ? `${theme.palette.common.red}` : `${theme.palette.text.primary}`,
                                color: errors?.otp ? `${theme.palette.common.red}` : `${theme.palette.text.primary}`,
                                outline: 'none',
                                border: `1px solid ${errors?.otp ? theme.palette.common.red : theme.palette.text.primary}`,
                            }}
                            renderSeparator={() => (
                                <span style={{ width: '6px' }}></span>
                            )}
                        // errorStyle={{
                        //     border: `2px solid ${theme.palette.error[500]}`,
                        //     color: theme.palette.error[500],
                        // }}
                        // focusStyle={{ outline: 'none' }}
                        // className="otp-input"
                        />
                        {!!errors?.otp && (
                            <FormHelperText
                                error
                                role="alert"
                                aria-live="assertive"
                                sx={{
                                    mt: 2,
                                    textAlign: 'center',
                                    fontSize: '14px'
                                }}
                            >
                                {errors?.otp}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "5px 0px 10px 0px",
                            [theme.breakpoints.down("sm")]: {
                                flexDirection: "column",
                                gap: "4px",
                                textAlign: "center",
                            },
                        }}
                    >
                        <OtpTimer onResend={handleResendOtp} disabled={isLoading || isSubmitting} />
                    </Box>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.error[500],
                            textAlign: "center",
                            marginBottom: "20px",
                            fontSize: { xs: "12px", sm: "14px" },
                            lineHeight: { xs: 1.2 },
                            maxWidth: { xs: "340px" },
                            mx: { xs: "auto" },
                        })}
                    >
                        {warningMessage}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <MFSubmitButton
                            label="Submit"
                            disabled={isLoading}
                            loadingEnable={isSubmitting}
                        />
                    </Box>

                    {/* Info section */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "10px",
                            gap: "5px",
                            justifyContent: { xs: "center" },
                            textAlign: { xs: "center" },
                            px: { xs: 1 },
                        }}
                    >
                        <InfoIconOutlined />
                        <Typography variant='overline_400'>
                            Keep Your Password/Pin/OTP private & confidential to avoid any misuse or unauthorised trades.
                        </Typography>
                    </Box>
                </Form>
            )}
        </Formik>
    )
}
